<template>
    <v-flex xs12>
      <v-card class="py-4">
        <v-flex xs12 px-4 v-if="err">
          <v-alert :value="true" type="error">{{err}}</v-alert>
        </v-flex>
        <v-flex xs12 px-4 v-if="success">
          <v-alert :value="true" type="success">{{success}}</v-alert>
        </v-flex>
        <v-form v-model="valid">
          <v-layout row wrap>
            <v-flex xs12 sm8 md6 lg4 px-4>
              <v-autocomplete
                v-if="!isLocalAdmin"
                required
                :disabled="isEdit"
                :loading="companyLoading"
                :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
                :items="companies"
                item-text="name"
                item-value="id"
                v-model="selectedCompany"
                :label="$lang.actions.selectCompany"
              />
              <v-autocomplete
                required
                :disabled="categories.length === 0"
                :loading="categoriesLoading"
                :rules="[v => !!v || `${$lang.errors.missingCategory}`]"
                :items="categories"
                :item-text="`name[${language}]`"
                item-value="id"
                v-model="selectedCategory"
                :label="$lang.actions.selectCategory"
              />
            </v-flex>
            <v-flex xs12 sm8 md6 lg4 mt-2 px-4>
              <v-checkbox
                class="mt-1"
                v-if="!isEdit"
                color="primary"
                v-model="multipleTables"
                :label="$lang.labels.multipleTables"
              />
              <v-text-field
                v-if="!multipleTables"
                hide-details
                v-model="sequenceNumber"
                :label="$lang.labels.sequenceNumber"
              />
              <v-text-field
                v-else
                hide-details
                v-model="numberOfTables"
                :label="$lang.labels.numberOfTables"
              />
              <v-text-field
                class="pt-4"
                v-model="seats"
                type="number"
                min="1"
                max="99"
                :rules="[rules.counter]"
                :label="$lang.labels.numberOfSeats"
              />
            </v-flex>
            <v-flex xs12 sm8 md6 lg4 mt-2 px-4>
              <v-text-field
                hide-details
                v-model="foreignId"
                :label="$lang.labels.foreignId"
              />
            </v-flex>
          </v-layout>
          <v-btn class="mx-4 primary" @click="addName" :disabled="editingName">{{$lang.actions.addTableName}}</v-btn>
          <div class="nameTable v-table__overflow px-4">
            <table class="v-datatable v-table theme--light">
              <tbody>
                <tr
                  v-for="(name, index) in names"
                  :key="index">
                  <template v-if="name.isEdit">
                    <td>
                      <v-select
                        class="min-w"
                        :items="languages"
                        item-text="text"
                        item-value="value"
                        v-model="tempName.language"
                        :label="$lang.actions.selectLanguage" />
                    </td>
                    <td>
                      <v-text-field
                        :placeholder="$lang.labels.name"
                        v-model="tempName.name"
                      /></td>
                    <td class="min-w-lg px-0">
                      <v-btn class="primary elevation-1" :disabled="!tempName.language || !tempName.name" @click="saveNameEdit(name)">{{$lang.actions.save}}</v-btn>
                      <v-btn class="error ml-0 elevation-1" @click="closeNameEdit(name)">{{$lang.actions.cancel}}</v-btn>
                    </td>
                  </template>
                  <template v-else>
                    <td>{{ name.language}}</td>
                    <td>{{ name.name}}</td>
                    <td class="actions px-0">
                      <v-btn class="primary elevation-1" @click="editName(name)">{{$lang.actions.edit}}</v-btn>
                      <v-btn class="error ml-0 elevation-1" @click="deleteName(index)">{{$lang.actions.delete}}</v-btn>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
          <v-layout class="px-2" row wrap justify-space-between align-center>
            <h3 class="pa-3">{{$lang.header.properties}}</h3>
            <v-flex xs12 px-3>
              <v-btn
                color="primary mx-0"
                @click="addPropertyCode()"
              >
                {{ $lang.labels.addProperty }}
              </v-btn>
            </v-flex>
            <v-flex xs12 px-3 pb-3>
              <v-data-table
                v-if="properties"
                :headers="headers"
                :items="properties"
                hide-actions
                class="elevation-0 mt-2">
                <template slot="items" slot-scope="props">
                  <td>{{ props.item.name }}</td>
                  <td v-if="['desktop.image', 'tablet.image'].includes(props.item.name)"><v-img :src="props.item.value" contain height="100"></v-img></td>
                  <td v-else>{{props.item.value}}</td>
                  <td><v-btn
                    class="primary mx-0"
                    @click="editPropertyCode(props.item)"
                  >
                    {{ $lang.actions.edit }}
                  </v-btn>
                    <v-btn
                      class="error mx-0"
                      @click="confirmDeletePropertyCode(props.item)"
                    >
                      {{ $lang.actions.delete }}
                    </v-btn></td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
          <v-layout row wrap justify-space-between class="px-2 mt-3">
            <v-text-field
              v-if="isEdit && createdOn"
              :label="$lang.labels.created"
              v-model="createdOn"
              readonly
              class="min-w max-w mx-3"
            />
            <v-text-field
              v-if="isEdit && modifiedOn"
              :label="$lang.labels.modified"
              v-model="modifiedOn"
              readonly
              class="min-w max-w mx-3"
            />
          </v-layout>
          <v-layout row wrap justify-space-between align-content-center class="px-2">
            <v-btn
              flat class="mx-3"
              @click="$router.push({ name: 'tables' })">
              <v-icon>keyboard_arrow_left</v-icon>
              {{$lang.actions.close}}
            </v-btn>
            <v-btn
              class="primary mx-3"
              :disabled="!valid"
              @click="submit">
              {{$lang.actions.submit}}
            </v-btn>
          </v-layout>
        </v-form>
      </v-card>
      <v-dialog persistent v-if="propertyModal" v-model="propertyModal" max-width="1200">
        <properties-modal :title="isPropertyEdit ? 'Edit Property' : 'Add Property'" :htmlCode="propertyCodeToEdit" :isHtmlEdit="isPropertyEdit" :htmlKeysListFiltered="propertiesKeysListFiltered" @submit="savePropertiesCode($event)" @close="propertyModal = false, propertyCodeToEdit = null, tempIndex = null"/>
      </v-dialog>
      <v-dialog persistent v-if="confirmationModal" v-model="confirmationModal" max-width="500">
        <confirmation-modal :text="$lang.actions.deleteProperty" @submit="deletePropertyCode()" @close="confirmationModal = false; tempIndex = null"/>
      </v-dialog>
    </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';
  import { baseUrl } from "../../assets/url";
  import * as fetch_data from "../../assets/fetch_data";

  export default {
    components: {
      propertiesModal: () => import('../modals/PropertiesModal'),
      confirmationModal: () => import('../modals/ConfirmationModal'),
    },
    data() {
      return {
        err: '',
        success: '',
        valid: false,
        companyLoading: true,
        categoriesLoading: false,
        search: '',
        companies: [],
        selectedCompany: null,
        categories: [],
        selectedCategory: null,
        selectedCompanyInfo: {},
        multipleTables: false,
        isEdit: false,
        language: this.$cookie.get('lang') || 'en',
        sequenceNumber: null,
        numberOfTables: null,
        seats: null,
        tempName: {
          language: '',
          name: '',
        },
        names: [],
        editingName: false,
        languages: JSON.parse(localStorage.languages),
        createdOn: '',
        modifiedOn: '',
        isLocalAdmin: true,
        rules: {
          required: value => !!value || 'Required.',
          counter: value => (value >= 1 && value <= 99) || 'Min is 1, max is 99',
          email: value => {
            const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return pattern.test(value) || 'Invalid e-mail.'
          },
        },
        properties: null,
        isPropertyEdit: false,
        propertyModal: false,
        propertyCodeToEdit: null,
        confirmationModal: false,
        tempIndex: null,
        propertiesKeysList: [
          'desktop.image',
          'desktop.x',
          'desktop.y',
          'desktop.sizePercentage',
          'tablet.image',
          'tablet.x',
          'tablet.y',
          'tablet.sizePercentage',
        ],
        headers: [
          {
            text: 'Name',
            value: 'name',
            align: 'left',
            sortable: true,
            width: '20%',
          },
          {
            text: 'Value',
            value: 'value',
            align: 'left',
            sortable: true,
            width: '60%',
          },
          {
            text: 'Actions',
            value: 'actions',
            align: 'left',
            sortable: false,
            width: '20%',
          },
        ],
        foreignId: null,
      };
    },
    computed: {
      propertiesKeysListFiltered() {
        if (this.properties) {
          const tempPropertiesHintsKeys =  this.properties.map(item => item.name);
          return this.propertiesKeysList.filter(item => !tempPropertiesHintsKeys.includes(item));
        }
        return this.propertiesKeysList;
      },
    },
    watch: {
      selectedCompany(val) {
        if (val) {
          this.fetchCompany();
        }
        this.fetchCategoriesforCompany(val);
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
    },
    methods: {
      addPropertyCode() {
        this.isPropertyEdit = false;
        this.propertyModal = true;
      },
      savePropertiesCode(item) {
        if (this.tempIndex !== null) {
          this.properties[this.tempIndex] = item;
        } else {
          if (!this.properties) {
            this.properties = []
          }
          this.properties.push(item);
        }
        this.tempIndex = null;
        this.propertyCodeToEdit = null;
        this.isPropertyEdit = false;
        this.propertyModal = false;
      },
      editPropertyCode(item) {
        this.tempIndex = this.properties.indexOf(item);
        this.propertyCodeToEdit = item;
        this.isPropertyEdit = true;
        this.propertyModal = true;
      },
      confirmDeletePropertyCode(item) {
        this.tempIndex = this.properties.indexOf(item);
        this.confirmationModal = true;
      },
      deletePropertyCode() {
        this.confirmationModal = false;
        this.properties.splice(this.tempIndex, 1);
        this.tempIndex = null;
      },
      formatDate(date) {
         return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      init() {
        return new Promise((resolve) => {
            this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                if (this.isLocalAdmin) this.selectedCompany = res.body.companies[0].id;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },

      fetchCategoriesforCompany(id) {
        this.categoriesLoading = true;
        this.categories = [];
        this.$http.post('tablecategory/getall', { companyId: id })
          .then((res) => {
            this.categoriesLoading = false;
            if (res.body.status === 'Success') {
              this.categories = res.body.details;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.categoriesLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },

      addName() {
        this.names.push({
          name: '',
          language: '',
          isEdit: true,
        });
        this.editingName = true;
        this.$forceUpdate();
      },

      editName(name) {
        this.tempName = {
          name: name.name,
          language: name.language,
        };
        this.$set(name, 'isEdit', true);
        this.editingName = true;
      },

      deleteName(index) {
        this.names.splice(index, 1);
        this.$forceUpdate();
      },

      saveNameEdit(name) {
        this.$set(name, 'name', this.tempName.name);
        this.$set(name, 'language', this.tempName.language);
        this.closeNameEdit(name);
      },

      closeNameEdit(name) {
        this.tempName = {
          name: '',
          language: '',
        };
        this.$set(name, 'isEdit', false);
        this.editingName = false;
      },

      requiredFieldsValidation(data) {
        return data.hasOwnProperty(this.selectedCompanyInfo.defaultLanguage);
      },

      async submit() {
        if (this.names.length === 0) {
          this.err = 'You must add name in at least one language';
          return;
        }
        const data = {
          categoryId: this.selectedCategory,
          name: {},
        };
        let url = this.isEdit ? `table/update/${this.$route.params.id}` : 'table/create';
        if (!this.isEdit) {
          data.companyId = this.selectedCompany;
        }
        if (!this.isEdit && this.multipleTables) {
          if (!this.numberOfTables) {
            this.err = 'Number of tables is required';
            return;
          }
          data.quantity = this.numberOfTables;
          url = 'table/createmultiple';
        } else {
          data.sequenceNumber = this.sequenceNumber;
        }
        data.seats = this.seats;
        if (this.properties) {
          data.properties = this.properties;
        }
        if (this.foreignId) {
          data.foreignId = this.foreignId;
        }
        this.names.forEach(name => data.name[name.language] = name.name);
        const isDefaultLangOk = await this.requiredFieldsValidation(data.name);
        if (!isDefaultLangOk) {
          this.err = `You must add name in Default language - ${this.selectedCompanyInfo.defaultLanguage}`;
          return;
        }
        this.$http.post(url, data)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.success = this.isEdit ? 'Table updated successfuly' : 'Table created successfuly';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                } else {
                  this.$router.push({ name: 'tables' });
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      fetchCompany(){
        fetch(`${baseUrl}/company/${this.selectedCompany}`, {
          method: 'get',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json;charset=UTF-8'
          },
        }).then((response) => {
          return response.json();
        }).then((data) => {
          if(data.error && data.error === 'Forbidden') {
            this.$router.push({name: 'logOut'})
            return;
          }
          this.selectedCompanyInfo = data.company;
        }).catch((e)=>{
          this.selectedCompanyInfo = fetch_data.company;
        })
      },
    },
    created() {
      this.isLocalAdmin = this.$auth.roles && this.$auth.roles.includes('ROLE_SUN_LOCAL_ADMIN');
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isEdit = true;
            this.$http.get(`table/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  const table = res.body.table;
                  this.selectedCompany = table.companyId;
                  this.selectedCategory = table.categoryId;
                  this.createdOn = this.formatDate(table.createdOn);
                  if (table.modifiedOn) {
                    this.modifiedOn = this.formatDate(table.modifiedOn);
                  }
                  this.names = Object.keys(table.name).map(lang => ({
                    language: lang,
                    name: table.name[lang],
                    isEdit: false,
                  }));
                  this.sequenceNumber = table.sequenceNumber;
                  this.seats = table.seats;
                  if (table.properties) {
                    this.properties = table.properties;
                  }
                  this.foreignId = table.foreignId;
                  this.$forceUpdate();
                  return;
                }
                this.err = res.body.statusText;
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              });
          }
        });
    }
  };
</script>

<style lang="scss" scoped>
.nameTable {
  tr {
    &:nth-of-type(odd) {
      background-color: #f9f9f9;
    }
  }

  td {
    border: 1px solid rgba(0, 0, 0, 0.2);
  }
}
</style>



// WEBPACK FOOTER //
// TableDetail.vue?f38d9454